/// <reference path="../../../node_modules/@arcgis/core/interfaces.d.ts" />
import React, { useEffect, useRef, useImperativeHandle, forwardRef } from "react";
import MapView from "@arcgis/core/views/MapView";
import WebMap from "@arcgis/core/WebMap";
import GraphicsLayer from "@arcgis/core/layers/GraphicsLayer";
import Graphic from "@arcgis/core/Graphic";
import * as geometryJsonUtils from "@arcgis/core/geometry/support/jsonUtils";
import SimpleFillSymbol from "@arcgis/core/symbols/SimpleFillSymbol";
import SimpleLineSymbol from "@arcgis/core/symbols/SimpleLineSymbol";
import Color from "@arcgis/core/Color";
import esriConfig from "@arcgis/core/config";

import LayerList from "@arcgis/core/widgets/LayerList";
import Legend from "@arcgis/core/widgets/Legend";
import BasemapGallery from "@arcgis/core/widgets/BasemapGallery";
import Basemap from "@arcgis/core/Basemap";
import TileLayer from "@arcgis/core/layers/TileLayer";
import MapImageLayer from "@arcgis/core/layers/MapImageLayer";
import FeatureLayer from "@arcgis/core/layers/FeatureLayer";
import Measurement from "@arcgis/core/widgets/Measurement";
import Print from "@arcgis/core/widgets/Print";
import Expand from "@arcgis/core/widgets/Expand";
import Home from "@arcgis/core/widgets/Home";
import ScaleBar from "@arcgis/core/widgets/ScaleBar";
import Zoom from "@arcgis/core/widgets/Zoom";

import * as webMercatorUtils from "@arcgis/core/geometry/support/webMercatorUtils";
import Point from "@arcgis/core/geometry/Point";

import { ArcGISMapProps, ArcGISMapHandle } from "../../types/config";

import { drawWidget } from "./custom-widgets/Draw";
import { googleStreetView } from "./custom-widgets/GoogleStreetView";
import { installCompactCoordinate } from "./custom-widgets/Coordinate";
import streetViewIcon from "./custom-widgets/images/googleStreetIcon.png"

import HarrisonImgThumb from "./images/HarrisonImgThumb.png";
import HarrisonBasemapThumb from "./images/HarrisonBasemapThumb.png";

import config from "../../config/config.json";
import "./styles/Map.css"
import * as CSS from "csstype";

// Self-host ArcGIS's runtime assets (locale strings, icons, wasm, worker chunks) from our
// own build output instead of the js.arcgis.com CDN default - see scripts/select-county.js's
// ensureArcgisAssets(), which copies node_modules/@arcgis/core/assets into public/assets.
esriConfig.assetsPath = `${process.env.PUBLIC_URL || ""}/assets`;

const MapDivStyle: CSS.Properties = { height: "100%", width: "100%", position: "relative" };

const ArcGISMap = forwardRef<ArcGISMapHandle, ArcGISMapProps>(({ itemId, onMapIdentify, onCondoUnitsFound, onLoadingStart, onLoadingEnd }, ref) => {
    const mapDiv = useRef<HTMLDivElement>(null);
    const viewRef = useRef<MapView | null>(null);
    const highlightLayerRef = useRef<GraphicsLayer | null>(null);

    useEffect(() => {
        if (!mapDiv.current) return;

        esriConfig.portalUrl = config.map.portalUrl;

        const webmap = new WebMap({
            portalItem: {
                id: itemId,
                portal: {
                    url: config.map.portalUrl
                }
            }
        });
        const view = new MapView({
            container: mapDiv.current,
            map: webmap,
            popupEnabled: false // Disable default popups
        });
        viewRef.current = view;

        const topRightRow = document.createElement("div");
        topRightRow.className = "custom-top-right-row";
        Object.assign(topRightRow.style, { display: "flex", gap: "8px", alignItems: "center", padding: "8px" });
        view.ui.add(topRightRow, { position: "top-right", index: 0 });

        const styleEl = document.createElement("style");
        styleEl.textContent = `
          .custom-top-right-row .expand-wrap { position: relative; }
          .custom-top-right-row .expand-wrap .esri-expand__panel {
            right: 0 !important; left: auto !important; top: calc(100% + 8px);
            max-height: min(60vh, calc(100% - 56px)); max-width: min(600px, calc(100vw - 32px));
            overflow: auto; box-sizing: border-box;
          }
          .custom-top-right-row .esri-widget--button { margin: 0; }
          .esri-ui, .esri-ui-corner, .esri-ui-top-right, .esri-ui-inner-container { overflow: visible !important; }
          .esri-layer-list, .esri-basemap-gallery { position: static !important; margin-top: 0 !important; }
          .esri-icon-layer-list, .esri-icon-basemap, .esri-icon-feature-layer, .esri-icon-radio-unchecked { position: static !important; top:auto !important; left:auto !important; }

          .exb-btn { width:32px; height:32px; border:1px solid #dcdcdc; border-radius:4px; background:#fff; cursor:pointer; display:grid; place-items:center; }
          .exb-btn svg { width:18px; height:18px; fill:#2b2b2b; }

          .exb-pop { z-index:5; border-radius:8px; box-shadow:0 8px 24px rgba(0,0,0,.15); padding:12px; }
          .exb-row { display:grid; grid-template-columns:auto 1fr auto; gap:8px; align-items:center; margin:8px 0; }
          .exb-sw { display:grid; grid-template-columns: repeat(10, 18px); gap:6px; margin:8px 0; }
          .exb-swatch { width:18px; height:18px; border-radius:50%; border:1px solid #ccc; cursor:pointer; }
          .exb-title { font-weight:600; margin-bottom:8px; display:flex; align-items:center; gap:8px; }
          .exb-prev { width:24px; height:24px; border-radius:50%; border:2px solid #2b2b2b; background:#0079c133; }

          .esri-button { padding:6px 10px; border:1px solid #dcdcdc; background:#fff; border-radius:4px; cursor:pointer; font-size:12px; }
          .esri-button:disabled { opacity:.5; cursor:default; }
        `;
        mapDiv.current.appendChild(styleEl);

        const wrap = () => {
            const w = document.createElement("div");
            w.className = "expand-wrap";
            topRightRow.appendChild(w);
            return w;
        };

        view.when(async () => {
            const hl = new GraphicsLayer({ listMode: "hide" });
            webmap.add(hl);
            highlightLayerRef.current = hl;

            const legend = new Legend({ view });
            const layerList = new LayerList({ view, visibilityAppearance: "checkbox" });

            // Map thumbnail filenames to imported images
            const thumbnailMap: Record<string, string> = {
                "images/HarrisonBasemapThumb.png": HarrisonBasemapThumb,
                "images/HarrisonImgThumb.png": HarrisonImgThumb,
            };

            // Create custom basemaps from config
            const customBasemaps = await Promise.all(config.basemapGallery.basemaps.map(async bmConfig => {
                const baseLayers = await Promise.all(bmConfig.layers.map(async layerConfig => {
                    // Try to load as TileLayer first
                    const tileLayer = new TileLayer({ url: layerConfig.url });
                    try {
                        await tileLayer.load();
                        // Check if it has tileInfo (is a cached service)
                        if (tileLayer.tileInfo) {
                            return tileLayer;
                        }
                    } catch (error) {
                        console.warn(`TileLayer failed for ${layerConfig.url}, trying MapImageLayer`);
                    }

                    // Fall back to MapImageLayer for dynamic services
                    return new MapImageLayer({ url: layerConfig.url });
                }));

                return new Basemap({
                    baseLayers: baseLayers,
                    title: bmConfig.title,
                    thumbnailUrl: thumbnailMap[bmConfig.thumbnailUrl] || bmConfig.thumbnailUrl
                });
            }));

            // Create BasemapGallery with custom + portal basemaps
            const basemapGallery = new BasemapGallery({ view });

            // Add custom basemaps once the portal basemaps have loaded. Was a setTimeout(500)
            // poll re-checking source.basemaps.length on every tick - each re-check re-touched
            // the source and was fanning out into repeated fetches of the same portal basemap
            // item (7x in one page load). source.load() is the actual "ready" signal.
            const source = basemapGallery.source as __esri.PortalBasemapsSource;
            try {
                await source.load();
                if (source.basemaps) {
                    customBasemaps.reverse().forEach((bm) => {
                        source.basemaps.unshift(bm);
                    });
                }
            } catch (error) {
                console.warn("Basemap gallery source failed to load", error);
            }

            const print = new Print({ view, printServiceUrl: config.exportWebMapService });

            const scaleBar = new ScaleBar({ view, unit: "dual", style: "ruler" });
            view.ui.add(scaleBar, { position: "bottom-left", index: 0 });

            view.ui.components = [];
            view.attributionVisible = true;

            const topLeftRow = document.createElement("div");
            topLeftRow.className = "custom-top-left-row";
            Object.assign(topLeftRow.style, {
                display: "flex",
                gap: "8px",
            });
            view.ui.add(topLeftRow, { position: "top-left", index: 0 });

            const zoomNode = document.createElement("div");

            topLeftRow.appendChild(zoomNode);

            const zoom = new Zoom({ view, container: zoomNode });

            const home = new Home({ view });
            view.ui.add(home, { position: "top-left", index: 1 });

            const gsv = googleStreetView(view, {
                iconUrl: streetViewIcon,
                startAtCenter: true,
                resizable: true
            });

            const draw = drawWidget(view);

            // Detect mobile for widget placement
            const isMobile = window.innerWidth <= 768;
            const widgetPlacement = isMobile ? "right" : "bottom";

            const drawExpand = new Expand({
                view,
                content: draw.element as any,
                group: "top-right-tools",
                expandTooltip: "Draw",
                expandIcon: "annotate-tool",
                collapseIcon: "annotate-tool",
                placement: "bottom",
                container: wrap(),
                mode: "floating"
            });

            // keep visibility in sync
            draw.setVisible(drawExpand.expanded);
            drawExpand.watch("expanded", (v: boolean) => draw.setVisible(!!v));

            const gsvExpand = new Expand({
                view,
                content: gsv.element as any,
                expandTooltip: "Street View",
                expanded: false,
                expandIcon: "person-view",
                collapseIcon: "person-view",
                mode: "floating"
            });
            view.ui.add(gsvExpand, { position: "top-left", index: 2 });
            gsv.setVisible(gsvExpand.expanded);
            gsvExpand.watch("expanded", (v) => gsv.setVisible(!!v));

            const measurementDiv = document.createElement("div");
            measurementDiv.style.minWidth = "fit-content";
            measurementDiv.style.minHeight = "fit-content";

            const measurement = new Measurement({
                view,
                container: measurementDiv,
                linearUnit: "feet",
                areaUnit: "acres",
            });

            const mBar = document.createElement("div");
            mBar.style.cssText = "display: flex; gap: 8px; margin: 8px; align-items: center;";

            const btnDist = document.createElement("button");
            btnDist.className = "esri-button";
            btnDist.title = "Distance";
            btnDist.setAttribute("aria-label", "Distance");
            btnDist.innerHTML = `<span class="esri-icon esri-icon-measure-line" aria-hidden="true"></span>`;

            const btnArea = document.createElement("button");
            btnArea.className = "esri-button";
            btnArea.title = "Area";
            btnArea.setAttribute("aria-label", "Area");
            btnArea.innerHTML = `<span class="esri-icon esri-icon-measure-area" aria-hidden="true"></span>`;

            const btnClear = document.createElement("button");
            btnClear.className = "esri-button";
            btnClear.title = "Clear";
            btnClear.setAttribute("aria-label", "Clear");
            btnClear.innerHTML = `<span class="esri-icon esri-icon-trash" aria-hidden="true"></span>`;

            mBar.append(btnDist, btnArea, btnClear);
            styleEl.textContent += `
                .esri-button { display:inline-grid; place-items:center; width:32px; height:32px; padding:0; }
                .esri-button .esri-icon { font-size:16px; line-height:1; }
                .custom-top-right-row .esri-button,
                .custom-top-right-row .esri-button:hover,
                .custom-top-right-row .esri-button:focus,
                .custom-top-right-row .esri-button:active {
                  color: #2b2b2b !important;
                  background: #fff !important;
                  border: 1px solid #dcdcdc !important;
                }

                .custom-top-right-row .esri-button:hover { background: #f5f5f5 !important; }
                .custom-top-right-row .esri-button[aria-pressed="true"] {
                  background: #e6f2fb !important;
                  color: #005e95 !important;
                  border-color: #9bc8e5 !important;
                }

                .custom-top-right-row .esri-button .esri-icon { font-size: 16px; line-height: 1; }
            `;

            const measureWrap = document.createElement("div");
            measureWrap.append(mBar, measurementDiv);

            const measureExpand = new Expand({
                view,
                content: measureWrap,
                group: "top-right-tools",
                expandTooltip: "Measure",
                expandIcon: "measure",
                collapseIcon: "measure",
                placement: widgetPlacement,
                container: wrap(),
                mode: "floating"
            });
            const legendExpand = new Expand({ view, content: legend, group: "top-right-tools", expandTooltip: "Legend", collapseIcon: "legend", placement: widgetPlacement, container: wrap(), mode: "floating" });
            const llExpand = new Expand({ view, content: layerList, group: "top-right-tools", expandTooltip: "Layers", collapseIcon: "layers", placement: widgetPlacement, container: wrap(), mode: "floating" });
            const bgExpand = new Expand({ view, content: basemapGallery, group: "top-right-tools", expandTooltip: "Basemaps", collapseIcon: "basemap", placement: widgetPlacement, container: wrap(), mode: "floating" });

            function setTool(tool: "distance" | "area" | null) {
                measurement.clear();
                (measurement as any).activeTool = tool;
            }

            const m = measurement as any;

            function startOrToggle(tool: "distance" | "area") {
                if (m.activeTool === tool) {
                    m.clear();
                    m.activeTool = null;
                    return;
                }

                m.clear();
                m.activeTool = null;
                requestAnimationFrame(() => {
                    m.activeTool = tool;
                });
            }

            btnDist.onclick = () => startOrToggle("distance");
            btnArea.onclick = () => startOrToggle("area");
            btnClear.onclick = () => {
                m.clear();
                m.activeTool = null;
            };

            m.watch("activeTool", (tool: "distance" | "area" | null) => {
                btnDist.setAttribute("aria-pressed", String(tool === "distance"));
                btnArea.setAttribute("aria-pressed", String(tool === "area"));
                view.popupEnabled = !tool;
                (view as any).__measuring = !!tool;
            });

            (measurement as any).watch("activeTool", (tool: "distance" | "area" | null) => {
                view.popupEnabled = !tool;
                (view as any).__measuring = !!tool;
            });

            measureExpand.watch("expanded", (open: boolean) => {
                if (open) {
                    requestAnimationFrame(() => m.renderNow?.());
                } else {
                    m.clear();
                    m.activeTool = null;
                    view.popupEnabled = true;
                    btnDist.setAttribute("aria-pressed", "false");
                    btnArea.setAttribute("aria-pressed", "false");
                }
            });

            const printExpand = new Expand({ view, content: print, group: "top-right-tools", expandTooltip: "Print", collapseIcon: "print", placement: widgetPlacement, container: wrap(), mode: "floating" });

            // Fix calcite-action button flex property to center icons
            function fixCalciteActionButtons() {
                const calciteActions = document.querySelectorAll('calcite-action');
                calciteActions.forEach((action) => {
                    const shadowRoot = (action as unknown as HTMLElement).shadowRoot;
                    if (shadowRoot) {
                        const button = shadowRoot.querySelector('.button');
                        if (button && !(button as any).__flexFixed) {
                            (button as HTMLElement).style.flex = 'none';
                            (button as HTMLElement).style.justifyContent = 'center';
                            (button as any).__flexFixed = true;
                        }
                    }
                });
            }

            // Run immediately and after delays to catch dynamically added elements
            setTimeout(fixCalciteActionButtons, 100);
            setTimeout(fixCalciteActionButtons, 500);
            setTimeout(fixCalciteActionButtons, 1000);

            // Watch for new calcite-action elements being added
            const observer = new MutationObserver(() => {
                fixCalciteActionButtons();
            });
            observer.observe(document.body, { childList: true, subtree: true });

            const coord = installCompactCoordinate(view, {
                decimals: 6,
                pinImageUrl: require("./custom-widgets/images/esriGreenPin16x26.png") as string,
                attachToUi: false
            });

            view.ui.add(coord.element as any, "manual");

            const pillEl = coord.element as HTMLElement;
            Object.assign(pillEl.style, { position: "absolute", left: "8px", bottom: "70px", zIndex: "5" });

            const vc = view.container as HTMLElement | null;
            const sbEl = vc ? (vc.querySelector(".esri-scale-bar") as HTMLElement | null) : null;

            function placeAboveScale() {
                if (!vc || !sbEl) return;
                const cr = vc.getBoundingClientRect();
                const sr = sbEl.getBoundingClientRect();
                const bottomDistance = Math.max(0, cr.bottom - sr.top);
                const gap = 12;
                pillEl.style.bottom = `${bottomDistance + gap}px`;
            }

            placeAboveScale();
            const ro1 = new ResizeObserver(placeAboveScale);
            const ro2 = new ResizeObserver(placeAboveScale);
            if (sbEl) ro1.observe(sbEl);
            if (vc) ro2.observe(vc);
            window.addEventListener("resize", placeAboveScale);
            const uiHandle = view.watch("size", placeAboveScale);

            (view as any).__widgetCleanup = () => {
                try { view.ui.remove(gsvExpand); } catch { }
                gsvExpand.destroy();
                gsv.destroy();

                printExpand.destroy(); legendExpand.destroy(); llExpand.destroy(); bgExpand.destroy();
                measureExpand.destroy(); measurement.destroy();
                print.destroy(); legend.destroy(); layerList.destroy(); basemapGallery.destroy();

                try { ro1.disconnect(); ro2.disconnect(); } catch { }
                uiHandle.remove();
                coord.destroy();

                scaleBar.destroy();
                styleEl.remove();
            };
        }, (err: any) => {
            if (err?.name === "AbortError" || err?.message === "Aborted") return;
            console.error("[ArcGISMap] view.when error", err);
        });

        return () => {
            const v = viewRef.current as any;
            if (v && typeof v.__widgetCleanup === "function") v.__widgetCleanup();
            view.destroy();
        };
    }, [itemId]);

    useImperativeHandle(ref, () => ({
        zoomTo({ latitude, longitude }) {
            viewRef.current?.goTo({ center: [longitude, latitude], zoom: 18 }).catch(() => { });
        },
        async highlightAndZoom(geomJson?: any, center?: { latitude: number; longitude: number }) {
            const view = viewRef.current;
            const hl = highlightLayerRef.current;
            if (!view || !hl) return;
            hl.removeAll();
            if (geomJson) {
                const geometry = geometryJsonUtils.fromJSON(geomJson);

                // If it's a point, query for the parcel at that location
                if (geometry?.type === "point") {
                    try {
                        const webmap = view.map as WebMap;
                        await webmap.loadAll();

                        webmap.allLayers.forEach((layer: any) => {
                            if (layer.labelingInfo) {
                                try {
                                    layer.labelsVisible = false;
                                } catch (e) {
                                    console.warn('Could not process layer labels', e);
                                }
                            }
                        });

                        const parcelLayer = webmap.allLayers.find((layer: any) => {
                            const url = layer.url || "";
                            return layer.title?.toLowerCase().includes("parcel");
                        }) as __esri.FeatureLayer;

                        if (parcelLayer) {
                            const query = parcelLayer.createQuery();
                            query.geometry = geometry;
                            query.spatialRelationship = "intersects";
                            query.returnGeometry = true;
                            query.outFields = ["*"];

                            const results = await parcelLayer.queryFeatures(query);

                            if (results.features.length > 0) {
                                const parcelGeometry = results.features[0].geometry;
                                const symbol = new SimpleFillSymbol({
                                    color: new Color([0, 121, 193, 0.2]),
                                    outline: new SimpleLineSymbol({
                                        color: new Color([0, 121, 193, 1]),
                                        width: 2
                                    }),
                                });
                                const g = new Graphic({ geometry: parcelGeometry, symbol });
                                hl.add(g);
                                view.goTo({ target: parcelGeometry as any, zoom: 18 }).catch(() => { });
                                return;
                            }
                        }
                    } catch (error) {
                        console.error("Error querying parcel:", error);
                    }

                    // Fallback: just zoom to the point
                    view.goTo({ target: geometry as any, zoom: 18 }).catch(() => { });
                    return;
                }

                // If it's already a polygon, just display it
                const symbol = new SimpleFillSymbol({
                    color: new Color([0, 121, 193, 0.2]),
                    outline: new SimpleLineSymbol({
                        color: new Color([0, 121, 193, 1]),
                        width: 2
                    }),
                });
                const g = new Graphic({ geometry, symbol });
                hl.add(g);
                view.goTo({ target: geometry as any, zoom: 18 }).catch(() => { });
                return;
            }
            if (center) {
                view.goTo({ center: [center.longitude, center.latitude], zoom: 18 }).catch(() => { });
            }
        },
        async queryParcelAtPoint(point: __esri.Point, view: MapView, hl: GraphicsLayer) {
            try {
                const webmap = view.map as WebMap;
                await webmap.loadAll();

                const parcelLayer = webmap.allLayers.find((layer: any) => {
                    const url = layer.url || "";
                    return layer.title?.toLowerCase().includes("parcel");
                }) as __esri.FeatureLayer;

                if (!parcelLayer) {
                    console.warn("Parcel layer not found");
                    view.goTo({ target: point as any, zoom: 18 }).catch(() => { });
                    return;
                }

                const query = parcelLayer.createQuery();
                query.geometry = point;
                query.spatialRelationship = "intersects";
                query.returnGeometry = true;
                query.outFields = ["*"];

                const results = await parcelLayer.queryFeatures(query);

                if (results.features.length > 0) {
                    const parcelGeometry = results.features[0].geometry;
                    const symbol = new SimpleFillSymbol({
                        color: new Color([0, 121, 193, 0.2]),
                        outline: new SimpleLineSymbol({
                            color: new Color([0, 121, 193, 1]),
                            width: 2
                        }),
                    });
                    const g = new Graphic({ geometry: parcelGeometry, symbol });
                    hl.add(g);
                    view.goTo({ target: parcelGeometry as any, zoom: 18 }).catch(() => { });
                } else {
                    view.goTo({ target: point as any, zoom: 18 }).catch(() => { });
                }
            } catch (error) {
                console.error("Error querying parcel:", error);
                view.goTo({ target: point as any, zoom: 18 }).catch(() => { });
            }
        }
    }));

    useEffect(() => {
        if (!viewRef.current) return;
        const view = viewRef.current;

        const isParcelOrBuilding = (layer: any): { ok: boolean; id?: number; title?: string } => {
            try {
                const title = (layer?.title ?? "").toString().toLowerCase();
                const url: string = String(layer?.url ?? "");
                const idPart = url.split("/").filter(Boolean).pop();
                const parsedId = Number(idPart);

                // Check against configured parcel layer URL (both base URL and with layer ID)
                const parcelBaseUrl = config.parcelLayer.replace(/\/\d+$/, '');
                const isParcel = url.includes(parcelBaseUrl) ||
                                 url.includes(config.parcelLayer) ||
                                 title.includes("parcel") ||
                                 title.includes("landroll");
                const isBuilding = title.includes("building");

                return { ok: isParcel || isBuilding, id: Number.isFinite(parsedId) ? parsedId : layer?.layerId, title: layer?.title };
            } catch {
                return { ok: false };
            }
        };

        const onClick = async (evt: __esri.ViewClickEvent) => {
            const v: any = view;
            if (!view || v.__measuring || v.__drawing) return;

            const mp = evt.mapPoint;
            if (!mp) return;

            // Only allow parcel identification when zoomed in enough
            if (view.scale > config.parcelScale) {
                return; // Too zoomed out, ignore click
            }

            const center = toLatLong(mp);

            // Show loading spinner
            onLoadingStart?.();

            // Always query the parcel feature service directly from config
            // This ensures we get the correct layer with all the proper field names
            let g: __esri.Graphic | undefined;
            let layer: any;
            let geomJson: any;

            try {
                // Step 1: Query the geometry layer (Layer 0 - Parcels) to get the parcel at the click point
                const geometryLayerUrl = config.geometryLayer || config.parcelLayer.replace(/\/\d+$/, '/0');
                const parcelGeometryLayer = new FeatureLayer({
                    url: geometryLayerUrl
                });

                await parcelGeometryLayer.load();

                const geometryQuery = parcelGeometryLayer.createQuery();
                geometryQuery.geometry = mp;
                geometryQuery.spatialRelationship = "intersects";
                geometryQuery.returnGeometry = true;
                geometryQuery.outFields = ["PARCEL_ID"];
                geometryQuery.num = 1;
                geometryQuery.outSpatialReference = mp.spatialReference;

                const geometryResults = await parcelGeometryLayer.queryFeatures(geometryQuery);

                if (geometryResults.features.length > 0) {
                    const parcelId = geometryResults.features[0].attributes?.PARCEL_ID;
                    const geometry = geometryResults.features[0].geometry;

                    if (parcelId) {
                        // Step 2: Query the attribute table (Layer 2 - LandRoll) to get full attributes
                        const landRollLayer = new FeatureLayer({
                            url: config.parcelLayer
                        });

                        await landRollLayer.load();

                        const attributeQuery = landRollLayer.createQuery();
                        attributeQuery.where = `PARCEL_ID = '${parcelId}'`;
                        attributeQuery.outFields = ["*"];
                        attributeQuery.returnGeometry = false;

                        const attributeResults = await landRollLayer.queryFeatures(attributeQuery);

                        if (attributeResults.features.length > 0) {
                            // Combine geometry from Layer 0 with attributes from Layer 2
                            g = new Graphic({
                                geometry: geometry,
                                attributes: attributeResults.features[0].attributes
                            });
                            layer = landRollLayer;

                            // Get geometry JSON for later use
                            geomJson = g.geometry?.toJSON?.();

                            // Check if this parcel is a condo parent
                            if (config.condoLayer && parcelId) {
                                try {
                                    // Query condo layer to see if this parcel has condo units
                                    const condoLayer = new FeatureLayer({
                                        url: config.condoLayer
                                    });

                                    await condoLayer.load();

                                    const condoQuery = condoLayer.createQuery();
                                    condoQuery.where = `PARENT_PAR = '${parcelId}'`;
                                    condoQuery.outFields = ["*"];
                                    condoQuery.returnGeometry = false;

                                    const condoResults = await condoLayer.queryFeatures(condoQuery);

                                    if (condoResults.features.length > 0) {
                                        // Get the parent address
                                        const parentAddress = g.attributes?.ADDR || g.attributes?.addCalc;

                                        if (parentAddress) {
                                            // Query all units with the same address from LandRoll
                                            const unitsQuery = landRollLayer.createQuery();
                                            unitsQuery.where = `ADDR = '${parentAddress}'`;
                                            unitsQuery.outFields = ["*"];
                                            unitsQuery.orderByFields = ["PARCEL_ID ASC"];

                                            const unitsResults = await landRollLayer.queryFeatures(unitsQuery);

                                            if (unitsResults.features.length > 0) {
                                                // Pass the condo units to Workspace to display in results grid
                                                onCondoUnitsFound?.(unitsResults.features.map(f => ({
                                                    ...f.attributes,
                                                    __center: center,
                                                    __geometry: geomJson
                                                })));
                                            }
                                        }
                                    }
                                } catch (condoError) {
                                    console.error('Error checking for condo units:', condoError);
                                }
                            }
                        } else {
                            // Fallback to geometry layer attributes if LandRoll query fails
                            g = geometryResults.features[0];
                            layer = parcelGeometryLayer;
                        }
                    }
                }
            } catch (error) {
                console.error('Error querying parcel layer:', error);
                onLoadingEnd?.();
                return;
            }

            if (!g) {
                onLoadingEnd?.();
                return;
            }

            // Set geomJson if not already set (may have been set during condo processing)
            if (!geomJson) {
                geomJson = g.geometry?.toJSON?.();
            }
            const layerInfo = isParcelOrBuilding(layer);

            let attrs = g.attributes ?? {};

            onMapIdentify?.({
                center,
                geometryJson: geomJson,
                attributes: attrs,
                layerId: layerInfo.id,
                layerTitle: layerInfo.title,
            });

            try {
                (ref as any)?.current?.highlightAndZoom?.(geomJson, center);
            } catch { }

            // Hide loading spinner - note that handleMapIdentify will also trigger loading
            // but that's okay because it manages its own busy state
            onLoadingEnd?.();
        };

        // Update cursor based on scale
        const updateCursor = () => {
            if (!view.container) return;
            if (view.scale <= config.parcelScale) {
                view.container.style.cursor = "pointer";
            } else {
                view.container.style.cursor = "default";
            }
        };

        // Set initial cursor
        updateCursor();

        // Update cursor on zoom
        const scaleWatchHandle = view.watch("scale", updateCursor);

        const clickHandle = view.on("click", onClick);
        return () => {
            clickHandle.remove();
            scaleWatchHandle.remove();
        };
    }, [itemId, onMapIdentify, onCondoUnitsFound]);

    return <div style={MapDivStyle} ref={mapDiv} />;
});

export default ArcGISMap;

function toLatLong(p: __esri.Point): { latitude: number; longitude: number } {
    const g = (p.spatialReference?.isWGS84 || p.spatialReference?.isGeographic)
        ? p
        : (webMercatorUtils.webMercatorToGeographic(p) as Point);

    return { latitude: g.y as number, longitude: g.x as number };
}
